// ============================================
// ContextoProyecto.js — CONTEXTO DEL PROYECTO inline (paso 1, sin modales)
// ============================================
// TAREAS-EXPEDIENTE (2026-07-10): la captura del paso 1 vive EN LA PAGINA de
// la vista Tareas (nada de ventanas emergentes): una seccion a la vez (patron
// de rondas integrado), con progreso y "Guardar y salir" siempre visible.
//
// LAS 5 PREGUNTAS (adaptativas):
//   P1 ¿Que necesita?            -> chips de VARIAS familias de trabajo (MULTI-INTERES
//      2026-10-17: un proyecto lleva CCTV + alarma + cerca; la primera marcada es la
//      principal). Escribe `familias` (tabla puente); el catalogo viejo `necesidad` se
//      CONGELA: ya no se escribe desde aqui, pero no se borra.
//   P2 ¿Donde se instalara?      -> botones AGRUPADOS POR RUBRO (tipos_lugar
//      por categoria, iconos Lucide, colapsables en celular) + aqui mismo la
//      ubicacion corta (tipo_ubicacion + especificacion) si NO hay sucursal.
//      Se OCULTA completa si el cliente ya tiene sucursal.
//   P3 ¿Como llego el cliente?   -> botones (origenes_lead). SOLO cliente NUEVO.
//   P4 Describe la necesidad     -> texto corto (pregunta adaptada por tipo).
//   P5 Notas privadas            -> opcional (misma ronda que P4).
//
// NADA es obligatorio; guarda parcial via PUT (solo campos cambiados, firmado
// por token). Si al guardar se cumple el requisito del paso 1, el backend
// avanza solo (avances_auto) y aqui se avisa.

import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, Save, Check, ClipboardList,
  Home, ShoppingCart, Factory, Building2, UtensilsCrossed, GraduationCap,
  Stethoscope, MapPin, Compass, StickyNote,
} from 'lucide-react';
import {
  API_BASE_CLI, FF,
  COLOR_BEEP_AZUL, COLOR_OSCURO, COLOR_BORDE, COLOR_GRIS, COLOR_GRIS_CL,
  authHeaders,
} from './helpersExpedientes';
import { TIPOS_UBICACION } from '../../utils/ubicacionExpediente';
import useFamilias from '../../hooks/useFamilias';

const COLOR_LIMA = '#94b803';

const inputBase = {
  width: '100%', padding: '11px 12px',
  border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
  // 16px: evita el auto-zoom de iOS al enfocar (leccion movil del proyecto).
  fontSize: 16, fontFamily: FF, color: COLOR_OSCURO,
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};

// RUBROS del catalogo de tipos de lugar (encabezados de grupo, iconos Lucide).
// La categoria viene de BD (mig_069); esto solo da etiqueta/icono/orden visual.
const RUBROS = {
  residencial:  { label: 'Residencial',             Icono: Home,            orden: 1 },
  retail:       { label: 'Retail y autoservicio',   Icono: ShoppingCart,    orden: 2 },
  industrial:   { label: 'Industrial y logistica',  Icono: Factory,         orden: 3 },
  corporativo:  { label: 'Corporativo',             Icono: Building2,       orden: 4 },
  hospitalidad: { label: 'Hospitalidad',            Icono: UtensilsCrossed, orden: 5 },
  educativo:    { label: 'Educativo',               Icono: GraduationCap,   orden: 6 },
  salud:        { label: 'Salud',                   Icono: Stethoscope,     orden: 7 },
  comercial:    { label: 'Comercial',               Icono: ShoppingCart,    orden: 8 }, // legacy
  otros:        { label: 'Otros',                   Icono: MapPin,          orden: 99 },
};

// Pregunta/placeholder de P4 segun el tipo de trabajo.
const NECESIDAD_POR_TIPO = {
  reparaciones: {
    titulo: 'Cual es la falla o el servicio?',
    placeholder: 'Equipo y falla reportada. Ej: Nobreak no enciende, hace bip continuo; revisar bateria...',
  },
  ventas: {
    titulo: 'Que producto busca?',
    placeholder: 'Ej: 12 camaras IP tipo bala, un NVR de 16 canales y 2 discos de 4TB...',
  },
  instalacion: {
    titulo: 'Describe la necesidad',
    placeholder: 'Alcance del proyecto. Ej: CCTV de 8 camaras exteriores, NVR 16ch, monitoreo en celular...',
  },
};
const NECESIDAD_DEFAULT = { titulo: 'Describe la necesidad', placeholder: 'Describe que busca el cliente...' };

export default function ContextoProyecto({ expediente, esCelular, onGuardado, onColapsar }) {
  const [catOrigenes, setCatOrigenes] = useState([]);
  const [catTiposLugar, setCatTiposLugar] = useState([]);
  const [preparando, setPreparando] = useState(true);
  const [esClienteNuevo, setEsClienteNuevo] = useState(true);
  const [tipoTrabajo, setTipoTrabajo] = useState(null);
  const [pasosFlujo, setPasosFlujo] = useState([]);
  // Grupos de rubros colapsados (solo celular arranca colapsado, salvo el 1o).
  const [gruposAbiertos, setGruposAbiertos] = useState(null); // null = aun no inicializado

  const [valores, setValores] = useState({
    // MULTI-INTERES: lista de rubros (la primera es la principal). Si el expediente viene de
    // antes y solo tiene la columna `familia`, se arranca con ella.
    familias: Array.isArray(expediente.familias) && expediente.familias.length
      ? expediente.familias
      : (expediente.familia ? [expediente.familia] : []),
    tipo_lugar: expediente.tipo_lugar || '',
    origen_lead: expediente.origen_lead || '',
    origen_detalle: expediente.origen_detalle || '',
    descripcion_necesidad: expediente.descripcion_necesidad || '',
    notas: expediente.notas || '',
    tipo_ubicacion: expediente.tipo_ubicacion || '',
    ubicacion_especificacion: expediente.ubicacion_especificacion || '',
  });
  const setV = (k, v) => setValores(prev => ({ ...prev, [k]: v }));

  const [ronda, setRonda] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  const tieneSucursal = !!expediente.cliente_sucursal_id;
  // Las 13 familias de trabajo (misma cache que el filtro por rubro y el selector del alta).
  const familiasCat = useFamilias();

  // Preparacion: catalogos + historial del cliente + flujo (en paralelo).
  useEffect(() => {
    let activo = true;
    (async () => {
      try {
        const [ol, tl, lista, flujo] = await Promise.all([
          fetch(`${API_BASE_CLI}/api/catalogos/origenes-lead`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE_CLI}/api/catalogos/tipos-lugar`).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE_CLI}/api/oportunidades?tipo=todos`, { headers: authHeaders() }).then(r => r.json()).catch(() => []),
          fetch(`${API_BASE_CLI}/api/oportunidades/${expediente.id}/flujo`, { headers: authHeaders() }).then(r => r.json()).catch(() => null),
        ]);
        if (!activo) return;
        setCatOrigenes(Array.isArray(ol) ? ol : []);
        setCatTiposLugar(Array.isArray(tl) ? tl : []);
        setTipoTrabajo(flujo && flujo.ok && flujo.workflow ? flujo.workflow.codigo : null);
        setPasosFlujo(flujo && flujo.ok && Array.isArray(flujo.pasos) ? flujo.pasos : []);
        // Cliente EXISTENTE = tiene OTRO expediente (mismo grupo/razon/sucursal/
        // persona). Fail-open a "nuevo" (mostrar de mas > ocultar de menos).
        const ids = ['cliente_grupo_id', 'cliente_razon_social_id', 'cliente_sucursal_id', 'cliente_persona_id'];
        const mios = ids.filter(k => expediente[k] != null);
        let existente = false;
        if (mios.length > 0 && Array.isArray(lista)) {
          existente = lista.some(e =>
            e.id !== expediente.id && mios.some(k => e[k] != null && String(e[k]) === String(expediente[k]))
          );
        }
        setEsClienteNuevo(!existente);
      } finally {
        if (activo) setPreparando(false);
      }
    })();
    return () => { activo = false; };
  }, [expediente]);

  // ---- RONDAS adaptativas
  const nec = NECESIDAD_POR_TIPO[tipoTrabajo] || NECESIDAD_DEFAULT;
  const rondas = useMemo(() => {
    const r = [{ id: 'necesidad', titulo: 'Que necesita?', Icono: ClipboardList }];
    if (!tieneSucursal) r.push({ id: 'lugar', titulo: 'Donde se instalara?', Icono: Building2 });
    if (esClienteNuevo) r.push({ id: 'origen', titulo: 'Como llego el cliente?', Icono: Compass });
    r.push({ id: 'descripcion', titulo: nec.titulo, Icono: StickyNote });
    return r;
  }, [tieneSucursal, esClienteNuevo, nec]);

  const total = rondas.length;
  const actual = rondas[Math.min(ronda, total - 1)] || rondas[0];
  const esUltima = ronda >= total - 1;

  // ---- Guardado: PUT con SOLO campos cambiados (0 llamadas si nada cambio).
  const guardar = async (colapsarDespues) => {
    if (guardando) return;
    const cambios = {};
    for (const k of Object.keys(valores)) {
      if (k === 'familias') continue; // lista: se compara aparte, abajo
      const original = expediente[k] || '';
      const nuevo = (valores[k] || '').toString();
      if (String(original) !== nuevo) cambios[k] = nuevo === '' ? null : valores[k];
    }
    // MULTI-INTERES: la lista de rubros va entera (orden incluido) solo si cambio.
    const famOriginal = Array.isArray(expediente.familias) && expediente.familias.length
      ? expediente.familias : (expediente.familia ? [expediente.familia] : []);
    if (JSON.stringify(valores.familias) !== JSON.stringify(famOriginal)) cambios.familias = valores.familias;
    if (Object.keys(cambios).length === 0) {
      if (colapsarDespues && typeof onColapsar === 'function') onColapsar();
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${expediente.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(cambios),
      }).then(x => x.json());
      if (r?.error) throw new Error(r.error);
      const avances = Array.isArray(r.avances_auto) ? r.avances_auto : [];
      if (avances.length > 0) {
        const ult = avances[avances.length - 1];
        const nom = (pasosFlujo.find(p => p.codigo === ult.a) || {}).nombre || ult.a;
        onGuardado?.(`Contexto guardado. El expediente avanzo solo al paso "${nom}"`);
      } else {
        onGuardado?.('Contexto guardado');
      }
      if (colapsarDespues && typeof onColapsar === 'function') onColapsar();
    } catch (e) {
      setError(e.message || 'No se pudo guardar el contexto');
    } finally {
      setGuardando(false);
    }
  };

  // ---- Piezas de UI
  const chip = (sel, color) => ({
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
    borderRadius: 10, minHeight: 44, textAlign: 'left', fontFamily: FF,
    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
    border: sel ? `2px solid ${color}` : `1px solid ${COLOR_BORDE}`,
    background: sel ? `${color}14` : '#fff',
    color: sel ? color : COLOR_OSCURO,
    transition: 'all 0.15s ease-out',
  });

  const renderChips = (lista, campo, color, columnas) => (
    <div style={{ display: 'grid', gap: 8, gridTemplateColumns: `repeat(${columnas}, minmax(0, 1fr))` }}>
      {lista.map(item => {
        const sel = valores[campo] === item.codigo;
        return (
          <button
            key={item.codigo}
            type="button"
            onClick={() => setV(campo, sel ? '' : item.codigo)}
            style={chip(sel, color)}
          >
            {item.icono && <span style={{ fontSize: 17, flexShrink: 0 }}>{item.icono}</span>}
            <span style={{ fontSize: 13.5, fontWeight: 600, minWidth: 0 }}>{item.nombre}</span>
          </button>
        );
      })}
    </div>
  );

  // Rubros ordenados con sus items (P2). En celular: colapsables (el rubro del
  // valor elegido —o el primero— abre; tocar el encabezado alterna).
  const rubrosOrdenados = useMemo(() => {
    const grupos = {};
    for (const t of catTiposLugar) {
      const cat = t.categoria || 'otros';
      if (!grupos[cat]) grupos[cat] = [];
      grupos[cat].push(t);
    }
    return Object.entries(grupos)
      .map(([cat, items]) => ({ cat, items, ...(RUBROS[cat] || RUBROS.otros) }))
      .sort((a, b) => a.orden - b.orden);
  }, [catTiposLugar]);

  useEffect(() => {
    if (gruposAbiertos !== null || rubrosOrdenados.length === 0) return;
    if (!esCelular) {
      setGruposAbiertos(new Set(rubrosOrdenados.map(r => r.cat))); // desktop: todo abierto
    } else {
      const catElegida = catTiposLugar.find(t => t.codigo === valores.tipo_lugar)?.categoria;
      setGruposAbiertos(new Set([catElegida || rubrosOrdenados[0].cat]));
    }
  }, [rubrosOrdenados, gruposAbiertos, esCelular, catTiposLugar, valores.tipo_lugar]);

  const toggleGrupo = (cat) => {
    setGruposAbiertos(prev => {
      const s = new Set(prev || []);
      if (s.has(cat)) s.delete(cat); else s.add(cat);
      return s;
    });
  };

  // MULTI-INTERES: chips de SELECCION MULTIPLE sobre las 13 familias. Tocar añade al final (o quita);
  // la primera de la lista es la PRINCIPAL y lleva su marca. Nada de "una sola opcion".
  const toggleFamilia = (id) => {
    setValores(prev => {
      const lista = Array.isArray(prev.familias) ? prev.familias : [];
      return { ...prev, familias: lista.includes(id) ? lista.filter(f => f !== id) : [...lista, id] };
    });
  };
  const renderChipsFamilias = () => {
    const elegidas = Array.isArray(valores.familias) ? valores.familias : [];
    return (
      <div>
        <div style={{ fontSize: 12.5, color: COLOR_GRIS, marginBottom: 10, lineHeight: 1.45 }}>
          Marca <b>todos los rubros</b> que lleva el proyecto. El primero que marques es el <b>principal</b>.
        </div>
        <div role="group" aria-label="Rubros del proyecto"
          style={{ display: 'grid', gap: 8, gridTemplateColumns: `repeat(${esCelular ? 1 : 2}, minmax(0, 1fr))` }}>
          {familiasCat.map(f => {
            const pos = elegidas.indexOf(f.id);
            const sel = pos >= 0;
            return (
              <button
                key={f.id}
                type="button"
                aria-pressed={sel}
                onClick={() => toggleFamilia(f.id)}
                style={chip(sel, COLOR_LIMA)}
              >
                <span style={{
                  width: 22, height: 22, borderRadius: 7, flexShrink: 0, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800,
                  background: sel ? COLOR_LIMA : '#f1f3f5', color: sel ? '#fff' : COLOR_GRIS_CL,
                }}>{sel ? pos + 1 : ''}</span>
                {/* En celular la marca va DEBAJO del nombre: en 360 px no caben nombre y marca en una linea */}
                <span style={{
                  display: 'flex', flexDirection: esCelular ? 'column' : 'row', alignItems: esCelular ? 'flex-start' : 'center',
                  gap: esCelular ? 3 : 8, flex: 1, minWidth: 0,
                }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, minWidth: 0, flex: esCelular ? 'none' : 1 }}>{f.etiqueta}</span>
                  {pos === 0 && (
                    <span style={{
                      fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: 'uppercase',
                      color: COLOR_LIMA, border: `1px solid ${COLOR_LIMA}`, borderRadius: 6, padding: '2px 6px', flexShrink: 0,
                    }}>Principal</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {familiasCat.length === 0 && (
          <div style={{ fontSize: 12.5, color: COLOR_GRIS_CL, padding: '8px 0' }}>Cargando los rubros...</div>
        )}
      </div>
    );
  };

  const cuerpoRonda = () => {
    switch (actual.id) {
      case 'necesidad':
        return renderChipsFamilias();
      case 'lugar':
        return (
          <>
            {rubrosOrdenados.map(({ cat, items, label, Icono }) => {
              const abierto = !gruposAbiertos || gruposAbiertos.has(cat);
              const tieneSeleccion = items.some(t => t.codigo === valores.tipo_lugar);
              return (
                <div key={cat} style={{ marginBottom: 10 }}>
                  <button
                    type="button"
                    onClick={() => toggleGrupo(cat)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      padding: '10px 2px', minHeight: 44, fontFamily: FF,
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <Icono size={16} color={tieneSeleccion ? COLOR_BEEP_AZUL : COLOR_GRIS_CL} />
                    <span style={{
                      fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                      color: tieneSeleccion ? COLOR_BEEP_AZUL : COLOR_GRIS_CL,
                    }}>{label}</span>
                    {tieneSeleccion && <Check size={14} color={COLOR_BEEP_AZUL} />}
                    <ChevronDown
                      size={16} color={COLOR_GRIS_CL}
                      style={{ marginLeft: 'auto', transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
                    />
                  </button>
                  {abierto && renderChips(items, 'tipo_lugar', COLOR_BEEP_AZUL, esCelular ? 2 : 3)}
                </div>
              );
            })}
            {/* Ubicacion corta (el dato que salio del modal de creacion): solo
                sin sucursal — con sucursal, ella manda y esta ronda ni aparece. */}
            <div style={{ marginTop: 6, borderTop: `1px dashed ${COLOR_BORDE}`, paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_GRIS, marginBottom: 8 }}>
                Donde queda? (opcional)
              </div>
              <div style={{ display: 'grid', gap: 10, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr' }}>
                <select
                  value={valores.tipo_ubicacion}
                  onChange={(e) => setV('tipo_ubicacion', e.target.value)}
                  style={{ ...inputBase, minHeight: 44 }}
                >
                  <option value="">Tipo de ubicacion...</option>
                  {TIPOS_UBICACION.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  type="text"
                  value={valores.ubicacion_especificacion}
                  onChange={(e) => setV('ubicacion_especificacion', e.target.value.slice(0, 200))}
                  placeholder="ej. Condesa, Torre B, Depto 4B"
                  maxLength={200}
                  style={{ ...inputBase, minHeight: 44 }}
                />
              </div>
            </div>
          </>
        );
      case 'origen':
        return (
          <>
            {renderChips(catOrigenes, 'origen_lead', COLOR_BEEP_AZUL, esCelular ? 2 : 3)}
            <input
              type="text"
              value={valores.origen_detalle}
              onChange={(e) => setV('origen_detalle', e.target.value.slice(0, 200))}
              placeholder="Detalle (opcional, ej: Recomendado por Juan Lopez)"
              style={{ ...inputBase, marginTop: 12, minHeight: 44 }}
            />
          </>
        );
      case 'descripcion':
        return (
          <>
            <textarea
              value={valores.descripcion_necesidad}
              onChange={(e) => setV('descripcion_necesidad', e.target.value)}
              placeholder={nec.placeholder}
              rows={3}
              style={{ ...inputBase, resize: 'vertical', minHeight: 80 }}
            />
            <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_GRIS, margin: '12px 0 6px' }}>
              Notas privadas (opcional)
            </div>
            <textarea
              value={valores.notas}
              onChange={(e) => setV('notas', e.target.value)}
              placeholder="Comentarios internos del contacto..."
              rows={2}
              style={{ ...inputBase, resize: 'vertical', minHeight: 56 }}
            />
          </>
        );
      default:
        return null;
    }
  };

  const IconoRonda = actual.Icono;
  const btnBase = {
    display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: FF,
    fontSize: 13, fontWeight: 700, minHeight: 44, borderRadius: 10,
    whiteSpace: 'nowrap', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  };

  return (
    <div style={{
      background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 14,
      padding: 16, marginBottom: 18, fontFamily: FF,
    }}>
      {/* Encabezado + progreso */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        {!preparando && IconoRonda && <IconoRonda size={18} color={COLOR_BEEP_AZUL} style={{ flexShrink: 0 }} />}
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, minWidth: 0 }}>
          {preparando ? 'Contexto del proyecto' : actual.titulo}
        </div>
        {!preparando && (
          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 700, color: COLOR_BEEP_AZUL, whiteSpace: 'nowrap' }}>
            Ronda {Math.min(ronda + 1, total)} de {total}
          </span>
        )}
      </div>
      {!preparando && (
        <div style={{ height: 4, background: '#e8ecf0', borderRadius: 99, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{
            height: '100%', width: `${((ronda + 1) / total) * 100}%`,
            background: COLOR_BEEP_AZUL, borderRadius: 99, transition: 'width 0.25s ease-out',
          }} />
        </div>
      )}

      {preparando ? (
        <div style={{ textAlign: 'center', padding: '26px 0', color: COLOR_GRIS_CL, fontSize: 13 }}>
          Preparando el contexto...
        </div>
      ) : cuerpoRonda()}

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
          padding: '8px 12px', borderRadius: 8, fontSize: 12, marginTop: 12,
        }}>{error}</div>
      )}

      {/* Navegacion: Atras / Guardar y salir / Siguiente-Terminar */}
      {!preparando && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
          <button
            onClick={() => setRonda(r => Math.max(0, r - 1))}
            disabled={guardando || ronda === 0}
            style={{
              ...btnBase, background: '#fff', color: COLOR_GRIS,
              border: `1px solid ${COLOR_BORDE}`, padding: esCelular ? '10px 10px' : '10px 14px',
              fontWeight: 600, opacity: ronda === 0 ? 0.45 : 1,
              cursor: (guardando || ronda === 0) ? 'not-allowed' : 'pointer',
            }}
          ><ChevronLeft size={16} /> Atras</button>

          <button
            onClick={() => guardar(true)}
            disabled={guardando}
            title="Guarda lo contestado y cierra (puedes retomar despues)"
            style={{
              ...btnBase, marginLeft: 'auto', gap: 6,
              background: '#fff', color: COLOR_BEEP_AZUL, border: `1px solid ${COLOR_BEEP_AZUL}`,
              padding: esCelular ? '10px 10px' : '10px 14px',
              opacity: guardando ? 0.6 : 1,
              cursor: guardando ? 'not-allowed' : 'pointer',
            }}
          ><Save size={15} /> {esCelular ? 'Guardar' : 'Guardar y salir'}</button>

          {esUltima ? (
            <button
              onClick={() => guardar(true)}
              disabled={guardando}
              style={{
                ...btnBase, gap: 6, background: COLOR_LIMA, color: '#fff', border: 'none',
                padding: esCelular ? '10px 12px' : '10px 16px',
                opacity: guardando ? 0.6 : 1,
                cursor: guardando ? 'not-allowed' : 'pointer',
              }}
            ><Check size={16} /> {guardando ? 'Guardando...' : 'Terminar'}</button>
          ) : (
            <button
              onClick={() => setRonda(r => Math.min(total - 1, r + 1))}
              disabled={guardando}
              style={{
                ...btnBase, background: COLOR_BEEP_AZUL, color: '#fff', border: 'none',
                padding: esCelular ? '10px 12px' : '10px 16px',
                cursor: guardando ? 'not-allowed' : 'pointer',
              }}
            >Siguiente <ChevronRight size={16} /></button>
          )}
        </div>
      )}
    </div>
  );
}
